<template>
    <div class="flow-intent-wrap">
        <or-alert
          v-if="flowNotExist && !maybeHasCorrespondingCustomTriggerNameFlow"
          type="error"
          :dismissible="false"
        >
          {{ $t('common.intent.types.flow_intent.notExist') }}
        </or-alert>
        <or-alert
          v-if="flowNotExist && maybeHasCorrespondingCustomTriggerNameFlow"
          type="error"
          :dismissible="false"
        >
          {{ $t('common.intent.types.flow_intent.reselectFlow') }}
        </or-alert>
        <div
          v-if="readonly"
          class="flow-intent--preview"
        >
            <div class="preview-header">
              <h5 class="preview-header--label">
                {{intent.label}}
              </h5>
              <or-icon-button
                v-if="!flowNotExist"
                icon="open_in_new"
                type="secondary"
                class="flat open-flow"
                size="small"
                @click="openUrl"
                disableRipple
              />
            </div>
            <div
              v-for="name in paramsList"
              :key="name"
              class="preview-body"
            >
              <template>
                <div class="intent-parameter">
                  <h6 class="intent-parameter--name">
                    {{name}}
                  </h6>
                  <div
                    v-if="!flowParameters[name] || flowParameters[name].type !== 'entity'"
                    class="intent-parameter--value"
                  >
                    {{flowParameters[name] ? flowParameters[name].value : '""'}}
                  </div>
                  <div
                    v-else
                    class="intent-parameter--value"
                  >
                      <div class="entity-type">
                          {{getSelectedEntityType(flowParameters[name].value)}}
                      </div>
                      <div class="entity-name">
                          {{getSelectedEntityName(flowParameters[name].value)}}
                      </div>
                  </div>
                </div>
              </template>
            </div>
        </div>
        <div v-else>
          <div class="selector-wrap">
              <or-select
                v-model="flowName"
                class="select-flow"
                has-search
                :placeholder="$t('common.intent.types.flow_intent.selectFlow')"
                :options="nluFlows.value"
                :disabled="nluFlows.loading || readonly"
                :error="$props.errors && $props.errors.name"
                :invalid="!!($props.errors && $props.errors.name)"
              />
              <or-progress-linear
                v-show="nluFlows.loading"
                class="select-loader"
                color="primary"
              />
              <div>
                  <span class="sidebar-helptext">{{ $t('common.intent.types.flow_intent.refreshFlowList') }}</span>
                  <or-icon-button
                    icon="refresh"
                    size="small"
                    type="secondary"
                    class="flat"
                    :loading="isRefreshingFlows"
                    @click="refreshFlowList"
                  />
              </div>
              <a
                  class="sidebar-helptext flow-link"
                  @click="openUrl"
              >
                {{ $t('common.intent.types.flow_intent.openFlow') }}
              </a>
          </div>
          <div
            v-if="showParams"
            class="params-list"
          >
            <param-input
              v-for="name in paramsList"
              :key="name"
              :label="name"
              :disabled="readonly"
              :value="flowParameters[name]"
              @input="updatedParam => onFlowParamsUpdate(name, updatedParam)"
              entity-selectable
            />
          </div>
        </div>
    </div>
</template>

<script>
import config from '@/config'
import { mapActions, mapGetters, mapState } from 'vuex'
import { getEntityTypeLabel } from '@/components/model/nlu-model/sidebar/entities/entity-types'
import paramInput from '../param_input'

export default {
  components: { paramInput },

  props: {
    intent: {
      label: String,
      name: String,
      params: Object,
      paramsType: Object
    },
    category: Object,
    readonly: Boolean,
    errors: Object
  },

  computed: {
    ...mapState('knowledgebase/entities', ['allEntities']),
    ...mapGetters('knowledgebase/flows', ['nluFlows']),

    flowName: {
      get () {
        return this.$props.intent.name
      },
      set (updatedName) {
        if (this.$props.intent.name !== updatedName) {
          this.selectFlow(updatedName)
        }
      }
    },

    flowParameters () {
      const parameters = {}
      Object.keys(this.intent.params).forEach(paramName => {
        parameters[paramName] = {
          value: this.intent.params[paramName],
          type: (this.intent.paramsType && this.intent.paramsType[paramName]) || 'custom'
        }
      })
      return parameters
    },

    showParams () {
      return !_.isEmpty(this.flowName)
    },

    intentParameters () {
      // Parameters returned with ""(double quotes) around them. To make them look nicer on UI re simply remove them, e.g.
      // { param1: '"value"' } => { param1: 'value' }
      return this.intent.params

      // Object.keys()
      //   .reduce((acc, paramKey) => {
      //     const param = this.intent.params[paramKey]
      //     acc[paramKey] = JSON.stringify(param)

      //     return acc
      //   }, {})
    },

    paramsList () {
      return _
        .chain(this.nluFlows.value)
        .find(['value', this.$props.intent.name])
        .get('params')
        .map('name')
        .value()
    },

    selectedFlow () {
      return _.find(this.nluFlows.value, ['value', this.$props.intent.name])
    },

    // (1) strict check. If this is true => flow 100% exists
    // (2) check non-strict check => if trigger name of the custom trigger name flows will be updated (1) will always yield false, though this flow can still exist
    maybeHasCorrespondingCustomTriggerNameFlow () {
      // Old flows were saved w/o id's. And we're trying to guess corresponding flow
      if (!this.$props.intent.id) {
        const customTriggerNameFlows = this.nluFlows.value.filter(flow => !flow.value.includes(flow.id))
        return customTriggerNameFlows.some(flow => flow.label === this.$props.intent.label)
      }
      return false
    },

    flowNotExist () {
      const exists = this.nluFlows.value.some(flow => {
        // Old flows were saved w/o id's
        if (this.$props.intent.id) {
          return flow.id === this.$props.intent.id
        }
        return this.$props.intent.name === flow.value
      })
      return !exists
    }
  },

  data () {
    return {
      isRefreshingFlows: false
    }
  },

  methods: {
    ...mapActions('knowledgebase/deployments', ['fetchActiveDeployments']),
    ...mapActions('knowledgebase/flows', ['fetchFlows']),

    getSelectedEntityType (entity) {
      let parsedEntity
      try {
        parsedEntity = JSON.parse(entity)
      } catch (error) {
        /** do nothing */
      }
      if (!parsedEntity) {
        return '-'
      }
      return getEntityTypeLabel(parsedEntity.type)
    },

    getSelectedEntityName (entity) {
      let parsedEntity
      try {
        parsedEntity = JSON.parse(entity)
      } catch (error) {
        /** do nothing */
      }
      if (!parsedEntity) {
        return '-'
      }
      let entityName = parsedEntity.name
      if (parsedEntity.role) {
        entityName += `:${parsedEntity.role}`
      }
      return entityName
    },

    async refreshFlowList () {
      this.isRefreshingFlows = true
      await Promise.all([
        this.fetchActiveDeployments(),
        this.fetchFlows()
      ])
      this.isRefreshingFlows = false
    },

    openUrl () {
      window.open(`${config.FLOW_BUILDER_URL}/#/flows/${this.selectedFlow.botId}/${this.selectedFlow.id}`)
    },

    selectFlow (flowName) {
      const selectedFlow = this.nluFlows.value.find(flow => flow.value === flowName)
      const flowParameters = selectedFlow.params.reduce((acc, param) => ({
        ...acc,
        [param.name]: '""'
      }), {})

      const flow = {
        id: selectedFlow.id,
        name: flowName,
        label: selectedFlow.label,
        params: flowParameters
      }

      this.$emit('update:intent', flow)
    },

    onFlowParamsUpdate (paramName, parameter) {
      const flow = {
        ...this.$props.intent,
        paramsType: {
          ...this.$props.intent.paramsType,
          [paramName]: parameter.type
        },
        params: {
          ...this.$props.intent.params,
          [paramName]: parameter.value
        }
      }
      this.$emit('update:intent', flow)
    }
  },

  watch: {
    readonly (value) {
      // whenever change to edit mode
      // reset params schema
      if (!value) {
        const flow = {
          ...this.$props.intent,
          params: _.pick(this.$props.intent.params, this.paramsList)
        }
        this.$emit('update:intent', flow)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
    .flow-intent-wrap {
        display: flex;
        flex-direction: column;
        flex-grow: 1;

        .params-list {
          border: solid 1px $or-grey;
          border-radius: 5px;
          padding: 20px 20px 20px 10px;
          margin: 10px 0;
          max-height: 200px;
          overflow: scroll;
        }

        .selector-wrap {
            display: flex;
            flex-grow: 1;
            flex-direction: column;

            .ui-select {
                margin-bottom: 0;
                flex-grow: 1;
            }

            .flow-link {
                color: $or-blue;

                &:hover {
                    cursor: pointer;
                }
            }
        }
        .select-flow{
            margin: 0;
        }
        .select-loader{
            margin-bottom: 1rem;
        }
    }

    .flow-intent--preview {
        .preview-header {
            display: flex;
            align-items: center;
            height: 24px;
        }
        .preview-header--label {
            margin: 0;
            color: rgb(30, 35, 43);
            font-size: 14px;
            font-weight: normal;
            line-height: 21px;
        }
    }

    .intent-parameter {
    }

    .intent-parameter:not(:first-child) {
        margin-top: 16px;
    }

    .intent-parameter--name {
        margin: 16px 5px 4px 0;
        color: rgb(145, 150, 157);
        font-size: 12px;
        font-weight: normal;
    }

    .intent-parameter--value {
        display: flex;
        color: rgb(30, 35, 43);
        font-size: 12px;
        font-weight: normal;
        line-height: 21px;
        word-break: break-all;
        .entity-type {
            margin-right: 8px;
            color: $or-grey;
            font-size: 14px;
        }
        .entity-name {
            font-size: 14px;
        }
    }
</style>
